import React, { useEffect } from 'react';
import { duckify, singleDuck, filterDuck } from "../../utils/duckify";
import { compose } from "redux";
import { withRouter } from "react-router";
import queryString from 'query-string';
import Select from 'react-select';
import { modals, sharedPortalSchools, sharedPortalUsers, sharedPortalSchoolGroups } from '../../ducks';
import Placeholder from '../Placeholder';
import PageWidget from './PageWidget';
import './styles/SchoolExplorer.scss';
import { isBrandedPortal } from "./utils/schools";

const SchoolExplorer = (props) => {
  const queryParams = queryString.parse(props.router.location.search);

  // filter selection
  const selectedGrades = queryParams.grades ? queryParams.grades.split(',') : [];
  const selectedPrograms = queryParams.programs ? queryParams.programs.split(',') : [];
  const isEmbed = queryParams.embed === 'true';

  let currentGroup = props.currentGroup;
  let activeGroup;
  let grades;
  let programs;

  // group selection
  const groups = props.groups ? props.groups : [];
  const groupUserPref = props.user.user_preference ? props.user.user_preference.default_shared_portal_group_id : null;
  const activeGroupId = queryParams.group || groupUserPref;

  if (currentGroup && isBrandedPortal(currentGroup, window.location.hostname)) {
    activeGroup = currentGroup;
    grades = currentGroup && currentGroup.grades ? currentGroup.grades : [];
    programs = currentGroup && currentGroup.programs ? currentGroup.programs : [];
  } else {
    activeGroup = activeGroupId ? groups.find((group) => group.id === activeGroupId) : null;
    grades = activeGroup && activeGroup.grades ? activeGroup.grades : [];
    programs = activeGroup && activeGroup.programs ? activeGroup.programs : [];
  }

  // school selection
  const activeSchool = queryParams.school ? props.schools.find((school) => school.id === queryParams.school) : null;

  const loggedIn = Object.keys(props.user).length !== 0;

  const onGroupSelect = (queryParams, groupId) => {
    const group = groups.find((group) => groupSearchId(group) === groupId);

    delete queryParams.school;
    delete queryParams.programs;
    delete queryParams.grades;

    updateQueryParams(props.router, queryParams, 'group', group.id);
  };

  const onGradeFilterSelect = (queryParams, selected) => {
    updateQueryParams(props.router, queryParams, 'grades', selected.map(grade => grade.value).join(','));

    return selected;
  };

  const onProgramFilterSelect = (queryParams, selected) => {
    updateQueryParams(props.router, queryParams, 'programs', selected.map(program => program.value).join(','));
    return selected;
  };

  const clearSelectedSchool = () => {
    delete queryParams.school;
    updateQueryParams(props.router, queryParams, 'group', activeGroup && activeGroup.id);
  }

  // This is a workaround to be able to search by Group Name or Location, as current version of react-select
  // only allows searching by the label or value of the option.
  const groupSearchId = (group) => {
    return group.id + group.name + group.location;
  };

  if (props.duckMeta.groups.ready && props.duckMeta.currentGroup.ready) {
    return <React.Fragment>
      {isBrandedPortal(activeGroup, window.location.hostname) && <h1>{activeGroup ? activeGroup.name : "Explore Schools"}</h1>}
      {!isBrandedPortal(activeGroup, window.location.hostname) && <h1>Explore Schools</h1>}

      <div className="sp-school-explorer">
        <div className={`sp-school-explorer-search-container ${activeSchool ? 'sp-active-school' : ''}`}>
          {!isBrandedPortal(activeGroup, window.location.hostname) && (
            <b>Select a School Group</b>
          )}

          {!isBrandedPortal(activeGroup, window.location.hostname) && (
            <div className={'sp-school-explorer-groups-select'}>
              <Select
                clearable={false}
                options={
                  groups.map((group) => ({ label: group.name, value: groupSearchId(group) }))
                }
                onChange={({ value }) => onGroupSelect(queryParams, value)}
                isSearchable
                value={activeGroup ? groupSearchId(activeGroup) : null}
                placeholder="Select a group"
              />
            </div>
          )}

          {activeGroup && (
            <div className="sp-school-explorer-search-filters">
              <div className="sp-school-explorer-search-filters-item">
                <b>Filter by Program</b>
                <div className={'sp-school-explorer-programs-select'}>
                  <Select
                    multi
                    clearable={false}
                    disabled={programs.length === 0}
                    options={
                      programs.map((program) => ({ label: program, value: program }))
                    }
                    onChange={selected => onProgramFilterSelect(queryParams, selected)}
                    value={selectedPrograms}
                    placeholder="Filter by program"
                  />
                </div>
              </div>

              <div className="sp-school-explorer-search-filters-item">
                <b>Filter by Grade</b>
                <div className={'sp-school-explorer-grades-select'}>
                  <Select
                    multi
                    clearable={false}
                    disabled={grades.length === 0}
                    options={
                      grades.map((grade) => ({ label: grade, value: grade }))
                    }
                    onChange={selected => onGradeFilterSelect(queryParams, selected)}
                    value={selectedGrades}
                    placeholder="Filter by grade"
                  />
                </div>
              </div>
            </div>
          )}

          <SchoolExplorerWrapper {...props} queryParams={queryParams} activeGroup={activeGroup} activeSchool={activeSchool} />
        </div>

        <ActiveSchoolComponent activeSchool={activeSchool} showApplyModal={props.showApplyModal} showMoreInfoModal={props.showMoreInfoModal} loggedIn={loggedIn} isEmbed={isEmbed} activeGroup={activeGroup} clearSelectedSchool={clearSelectedSchool} />
      </div>
    </React.Fragment>
  }
  return <Placeholder.loading ready={props.schools.ready}>Loading School Explorer...</Placeholder.loading>;
};

const SchoolExplorerWrapper = ({ queryParams, activeGroup, activeSchool, ...props }) => {
  if (props.schools.ready && props.duckMeta.user.ready) {
    return (
      <React.Fragment>
        <SchoolExplorerComponent
          schools={props.schools}
          router={props.router}
          showApplyModal={props.showApplyModal}
          showMoreInfoModal={props.showMoreInfoModal}
          currentGroup={props.currentGroup}
          queryParams={queryParams}
          activeGroup={activeGroup}
          activeSchool={activeSchool}
        />
      </React.Fragment>
    )
  }
  return <Placeholder.loading ready={props.schools.ready}>Loading School Explorer...</Placeholder.loading>;
};

const SchoolExplorerComponent = ({ schools: allSchools, router, queryParams, activeGroup, activeSchool, showApplyModal }) => {
  // filter schools by group selection
  const schools = activeGroup ? allSchools.filter((school) => school.shared_portal_groups_ids.includes(activeGroup.id)) : [];
  const basePath = '/explore_schools';

  const onSchoolSelect = (queryParams, schoolId) => {
    const path = activeGroup ? `${basePath}?school=${schoolId}&group=${activeGroup.id}` : `${basePath}?school=${schoolId}`;
    router.push(path);

    if (activeGroup) {
      updateQueryParams(router, queryParams, 'school', schoolId);
      updateQueryParams(router, queryParams, 'group', activeGroup.id);
    } else {
      updateQueryParams(router, queryParams, 'school', schoolId);
    }
  };

  useEffect(() => {
    if (queryParams.applying === 'true') {
      // Clear so it doesn't reopen popup if you refresh
      delete queryParams.applying;
      const path = `${basePath}?${queryString.stringify(queryParams)}`;
      router.push(path);
      if (activeSchool) {
        showApplyModal(activeSchool.customer_id);
      }
    }
  }, [queryParams.applying]);

  return <React.Fragment>
    <div className='sp-explorer-schools-table'>
      <table className="sp-school-explorer-search-results">
        <tbody>
          {schools.map((school) => (
            <tr key={school.id} className={`sp-school-explorer-search-results-school ${(activeSchool && activeSchool.id === school.id) ? 'active' : ''}`} onClick={() => onSchoolSelect(queryParams, school.id)}>
              <td className='sp-school-explorer-search-results-school-logo-container'>
                <img className="sp-school-explorer-search-results-school-logo" src={school.shared_portal_logo_url}></img>
              </td>
              <td className='sp-school-explorer-search-results-school-details'>
                <div className="sp-school-explorer-search-results-school-name">{school.school_name}</div>
                <div className="sp-school-explorer-search-results-school-address">{school.address}</div>
              </td>
              <td className='sp-school-explorer-search-results-school-grade'>
                <div className="sp-school-explorer-search-results-school-grade-label">Grade Level</div>
                <div className="sp-school-explorer-search-results-school-grade-value">{school.min_grade}-{school.max_grade}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </React.Fragment >
};

const ActiveSchoolComponent = ({ activeGroup, activeSchool, showApplyModal, showMoreInfoModal, loggedIn, isEmbed, clearSelectedSchool }) => {
  const buttonCss = 'sp-school-explorer-school-details-actions-button';

  return <React.Fragment>
    {
      activeSchool && <div className='sp-school-explorer-school-details-container'>
        <div className="sp-school-explorer-school-details">
          <img className="sp-school-explorer-school-details-header-logo"
            src={activeSchool.shared_portal_logo_url}></img>
          <div className="sp-school-explorer-school-details-body">
            <h1 className='header-school-name'>{activeSchool.school_name}</h1>
            <span>{activeSchool.description}</span>
          </div>
          <div className="sp-school-explorer-school-details-actions">
            {loggedIn
              ? (
                <>
                  {activeSchool.applications_enabled && <button className={`btn btn-primary ${buttonCss}-apply`} onClick={() => showApplyModal(activeSchool.customer_id)}>Apply Now</button>}
                  <button className={`btn btn-light ${buttonCss}-info`} onClick={() => showMoreInfoModal(activeSchool.customer_id, activeSchool.info_url)}>More Information</button>
                </>
              )
              : <>{activeSchool.applications_enabled && <a className={`btn btn-primary ${buttonCss}-apply`} href={`/explore_schools_private?school=${activeSchool.id}&group=${activeGroup.id}&applying=true`} target={isEmbed ? '_blank' : '_self'}>Apply Now</a>}</>
            }
          </div>
        </div>

        {activeSchool.programs.length !== 0 && <div className="sp-school-explorer-school-details-programs">
          <h3>Programs</h3>
          <div className="sp-school-explorer-badge-list">
            {activeSchool.programs.toSorted().map(
              program => <span key={program} className="badge bg-success">{program}</span>
            )}
          </div>
        </div>}

        <PageWidget activeSchool={activeSchool} />
        <button className='sp-back-to-list-button btn btn-secondary' onClick={clearSelectedSchool}>
          <i className="fa fa-arrow-left"/>&nbsp;Back to School List
        </button>
      </div>
    }
  </React.Fragment>
};

const updateQueryParams = (router, queryParams, key, value) => {
  queryParams[key] = value;
  const queryString = Object.keys(queryParams).map(key => key + '=' + queryParams[key]).join('&');
  const basePath = '/explore_schools';
  router.push(`${basePath}?${queryString}`);
};

const mapDispatchToProps = (dispatch) => ({
  showApplyModal: (customerId) => dispatch(modals.actions.show('ApplyNow', { customerId })),
  showMoreInfoModal: (customerId, moreInfoUrl) => dispatch(modals.actions.show('MoreInfo', { customerId, moreInfoUrl }))
});

const mapDucksToProps = ({ location, _params }) => {
  const query = queryString.parse(location.search);

  // removes school from query to avoid extra API calls
  delete query.school;

  return {
    schools: filterDuck(sharedPortalSchools, query),
    user: singleDuck(sharedPortalUsers, 'current'),
    groups: sharedPortalSchoolGroups,
    currentGroup: singleDuck(sharedPortalSchoolGroups, 'current')
  }
};

export default compose(
  withRouter,
  duckify(mapDucksToProps, () => { }, mapDispatchToProps)
)(SchoolExplorer);
